import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../api/axiosConfig';
import {
  feedbackSessionAuthenticated,
  type UserFeedbackSource,
} from '../../api/userFeedbackApi';
import './userFeedbackLauncher.css';

export type FeedbackTrigger = 'MANUAL' | 'MAIN_EXIT_INTENT' | 'PANEL_CLOSE';
export type FeedbackAnswer = 'GOT_IT' | 'UNCLEAR' | 'NOT_FOUND';
export type FeedbackReason =
  | 'JUDGMENT_UNCLEAR'
  | 'EVIDENCE_WEAK'
  | 'DATA_INSUFFICIENT'
  | 'TOO_MUCH_INFO'
  | 'MISSING_FEATURE'
  | 'TRUST_LOW'
  | 'OTHER';

type FeedbackContextValue = string | number | boolean | null | undefined;
export type FeedbackContext = Record<string, FeedbackContextValue>;

type FeedbackRequestDetail = {
  source: UserFeedbackSource;
  trigger?: FeedbackTrigger;
  ticker?: string;
  context?: FeedbackContext;
  closeWindowAfter?: boolean;
};

type Props = {
  source: UserFeedbackSource;
  variant?: 'floating' | 'panel';
  ticker?: string;
  context?: FeedbackContext;
  enableExitIntent?: boolean;
};

const REQUEST_EVENT = 'policyintel:feedback-request';
const AUTO_PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const SUBMITTED_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MAIN_EXIT_MIN_DWELL_MS = 20_000;
const PANEL_CLOSE_MIN_DWELL_MS = 15_000;

const ANSWERS: Array<{ value: FeedbackAnswer; label: string; sub: string }> = [
  { value: 'GOT_IT', label: '얻었다', sub: '원하는 판단을 확인함' },
  { value: 'UNCLEAR', label: '애매했다', sub: '봤지만 확신이 안 듦' },
  { value: 'NOT_FOUND', label: '못 얻었다', sub: '원하는 답이 없었음' },
];

const REASONS: Array<{ value: FeedbackReason; label: string }> = [
  { value: 'JUDGMENT_UNCLEAR', label: '판단이 이해 안 됨' },
  { value: 'EVIDENCE_WEAK', label: '근거가 부족함' },
  { value: 'DATA_INSUFFICIENT', label: '데이터가 부족함' },
  { value: 'TOO_MUCH_INFO', label: '정보가 너무 많음' },
  { value: 'MISSING_FEATURE', label: '원하는 기능이 없음' },
  { value: 'TRUST_LOW', label: '신뢰하기 어려움' },
  { value: 'OTHER', label: '기타' },
];

const answerLabel = (value: FeedbackAnswer | '') =>
  ANSWERS.find((item) => item.value === value)?.label ?? '';

const reasonLabel = (value: FeedbackReason | '') =>
  REASONS.find((item) => item.value === value)?.label ?? '';

const storageKey = (source: UserFeedbackSource, kind: 'prompt' | 'submitted') =>
  `policyintel.feedback.${source}.${kind}`;

const safeStorageNumber = (key: string): number => {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = window.localStorage.getItem(key);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
};

const writeStorageNumber = (key: string, value: number) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Storage 제한 환경에서도 피드백 자체는 계속 동작한다.
  }
};

const feedbackAnonSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  const key = 'policyintel.feedback.anonSessionId';
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const generated = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `fb-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(key, generated);
    return generated;
  } catch {
    return `fb-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
};

const compactContext = (context?: FeedbackContext): FeedbackContext => {
  const out: FeedbackContext = {};
  Object.entries(context ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    out[key] = value;
  });
  return out;
};

/**
 * LiveSummary close처럼 컴포넌트 밖에서 피드백 다이얼로그를 요청할 때 사용한다.
 * true면 현재 window의 UserFeedbackLauncher가 요청을 받았다는 뜻이다.
 */
export function requestUserFeedback(detail: FeedbackRequestDetail): boolean {
  if (typeof window === 'undefined') return false;
  const event = new CustomEvent<FeedbackRequestDetail>(REQUEST_EVENT, {
    detail,
    cancelable: true,
  });
  return !window.dispatchEvent(event);
}

export default function UserFeedbackLauncher({
  source,
  variant = 'floating',
  ticker,
  context,
  enableExitIntent = source === 'MAIN',
}: Props) {
  const authenticated = feedbackSessionAuthenticated();
  const mountedAtRef = useRef(Date.now());
  const closeWindowAfterRef = useRef(false);
  const requestContextRef = useRef<FeedbackContext>({});
  const requestTickerRef = useRef('');

  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<FeedbackTrigger>('MANUAL');
  const [answer, setAnswer] = useState<FeedbackAnswer | ''>('');
  const [reason, setReason] = useState<FeedbackReason | ''>('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');

  const baseContext = useMemo(() => compactContext(context), [context]);

  const canShowAutomaticPrompt = useCallback((nextTrigger: FeedbackTrigger) => {
    if (typeof window === 'undefined') return false;
    const dwellMs = Date.now() - mountedAtRef.current;
    const minDwell = nextTrigger === 'PANEL_CLOSE'
      ? PANEL_CLOSE_MIN_DWELL_MS
      : MAIN_EXIT_MIN_DWELL_MS;
    if (dwellMs < minDwell) return false;

    const now = Date.now();
    const lastSubmitted = safeStorageNumber(storageKey(source, 'submitted'));
    if (lastSubmitted && now - lastSubmitted < SUBMITTED_COOLDOWN_MS) return false;

    const lastPrompt = safeStorageNumber(storageKey(source, 'prompt'));
    if (lastPrompt && now - lastPrompt < AUTO_PROMPT_COOLDOWN_MS) return false;
    return true;
  }, [source]);

  const resetForm = useCallback(() => {
    setAnswer('');
    setReason('');
    setMessage('');
    setStatus('');
  }, []);

  const openPrompt = useCallback((
    nextTrigger: FeedbackTrigger,
    options?: { ticker?: string; context?: FeedbackContext; closeWindowAfter?: boolean; automatic?: boolean },
  ) => {
    const automatic = options?.automatic ?? nextTrigger !== 'MANUAL';
    if (automatic && !canShowAutomaticPrompt(nextTrigger)) {
      if (options?.closeWindowAfter && typeof window !== 'undefined') window.close();
      return false;
    }

    resetForm();
    setTrigger(nextTrigger);
    requestTickerRef.current = String(options?.ticker ?? ticker ?? '').trim();
    requestContextRef.current = compactContext({ ...baseContext, ...(options?.context ?? {}) });
    closeWindowAfterRef.current = Boolean(options?.closeWindowAfter);
    setOpen(true);

    if (automatic) {
      writeStorageNumber(storageKey(source, 'prompt'), Date.now());
    }
    return true;
  }, [baseContext, canShowAutomaticPrompt, resetForm, source, ticker]);

  const finishClose = useCallback(() => {
    const closeWindowAfter = closeWindowAfterRef.current;
    closeWindowAfterRef.current = false;
    setOpen(false);
    setStatus('');
    if (closeWindowAfter && typeof window !== 'undefined') {
      window.setTimeout(() => window.close(), 0);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onRequest = (event: Event) => {
      const custom = event as CustomEvent<FeedbackRequestDetail>;
      const detail = custom.detail;
      if (!detail || detail.source !== source) return;
      custom.preventDefault();
      openPrompt(detail.trigger ?? 'MANUAL', {
        ticker: detail.ticker,
        context: detail.context,
        closeWindowAfter: detail.closeWindowAfter,
        automatic: (detail.trigger ?? 'MANUAL') !== 'MANUAL',
      });
    };

    window.addEventListener(REQUEST_EVENT, onRequest as EventListener);
    return () => window.removeEventListener(REQUEST_EVENT, onRequest as EventListener);
  }, [openPrompt, source]);

  useEffect(() => {
    if (!enableExitIntent || source !== 'MAIN' || typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined;
    }
    if (typeof window.matchMedia === 'function' && !window.matchMedia('(pointer:fine)').matches) {
      return undefined;
    }

    const onMouseOut = (event: MouseEvent) => {
      if (open || submitting) return;
      if (event.relatedTarget || event.clientY > 4) return;
      openPrompt('MAIN_EXIT_INTENT', { automatic: true });
    };

    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, [enableExitIntent, open, openPrompt, source, submitting]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submitting) finishClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [finishClose, open, submitting]);

  const submit = useCallback(async () => {
    if (!answer) {
      setStatus('먼저 한 가지를 선택해주세요.');
      return;
    }
    if (answer !== 'GOT_IT' && !reason) {
      setStatus('가장 아쉬웠던 이유를 한 가지 선택해주세요.');
      return;
    }
    const safeMessage = message.trim();
    if (safeMessage.length > 3000) {
      setStatus('피드백은 3000자 이하로 입력해주세요.');
      return;
    }

    const dwellSeconds = Math.max(0, Math.round((Date.now() - mountedAtRef.current) / 1000));
    const payloadContext = compactContext({
      ...baseContext,
      ...requestContextRef.current,
      authenticated,
    });
    const fallbackDescription = [answerLabel(answer), reason ? reasonLabel(reason) : ''].filter(Boolean).join(' · ');

    const form = new FormData();
    form.append('title', `사용자 피드백 · ${source} · ${trigger}`);
    form.append('description', safeMessage || fallbackDescription || '사용자 피드백');
    form.append('source', source);
    form.append('trigger', trigger);
    form.append('answer', answer);
    if (reason) form.append('reason', reason);
    if (requestTickerRef.current) form.append('ticker', requestTickerRef.current);
    form.append('dwellSeconds', String(dwellSeconds));
    form.append('context', JSON.stringify(payloadContext));
    form.append('pagePath', `${window.location.pathname}${window.location.search}`);
    form.append('anonSessionId', feedbackAnonSessionId());
    form.append('browserInfo', window.navigator.userAgent);
    form.append('includeTechnicalInfo', 'true');

    setSubmitting(true);
    setStatus('');
    try {
      await api.post('/support/feedback', form, {
        headers: { 'Content-Type': null },
      });
      writeStorageNumber(storageKey(source, 'submitted'), Date.now());
      setStatus('의견을 남겨주셔서 감사합니다.');
      const closeWindowAfter = closeWindowAfterRef.current;
      window.setTimeout(() => {
        if (closeWindowAfter) {
          closeWindowAfterRef.current = false;
          window.close();
          return;
        }
        setOpen(false);
        setStatus('');
      }, 650);
    } catch (e: any) {
      setStatus(
        e?.response?.data?.message
          ?? e?.response?.data?.detail
          ?? '피드백을 보내지 못했습니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [answer, authenticated, baseContext, message, reason, source, trigger]);

  const title = trigger === 'PANEL_CLOSE'
    ? '닫기 전에 하나만 알려주세요'
    : trigger === 'MAIN_EXIT_INTENT'
      ? '나가기 전에 하나만 알려주세요'
      : '사용 경험을 알려주세요';

  return (
    <>
      <button
        type="button"
        className={`policy-feedback-trigger is-${variant}`}
        onClick={() => openPrompt('MANUAL', { automatic: false })}
        aria-haspopup="dialog"
      >
        <span aria-hidden="true">💬</span>
        <b>피드백</b>
      </button>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="policy-feedback-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !submitting) finishClose();
              }}
            >
              <section
                className="policy-feedback-dialog is-quick"
                role="dialog"
                aria-modal="true"
                aria-labelledby="policy-feedback-title"
              >
                <header className="policy-feedback-dialog__header">
                  <div className="policy-feedback-dialog__heading">
                    <small>POLICYINTEL BETA · 10초 피드백</small>
                    <h2 id="policy-feedback-title">{title}</h2>
                    <p>로그인 없이 답할 수 있습니다. 제품에서 무엇을 먼저 고쳐야 하는지 결정하는 데 사용합니다.</p>
                  </div>
                  <button
                    type="button"
                    className="policy-feedback-close"
                    onClick={finishClose}
                    disabled={submitting}
                    aria-label="피드백 닫기"
                  >
                    ×
                  </button>
                </header>

                <div className="policy-feedback-body">
                  <fieldset>
                    <legend>오늘 원하는 답을 얻으셨나요?</legend>
                    <div className="policy-feedback-answer-grid">
                      {ANSWERS.map((item) => (
                        <button
                          type="button"
                          key={item.value}
                          className={answer === item.value ? 'is-selected' : ''}
                          onClick={() => {
                            setAnswer(item.value);
                            if (item.value === 'GOT_IT') setReason('');
                            setStatus('');
                          }}
                        >
                          <b>{item.label}</b>
                          <span>{item.sub}</span>
                        </button>
                      ))}
                    </div>
                  </fieldset>

                  {answer && answer !== 'GOT_IT' ? (
                    <fieldset className="policy-feedback-reason-fieldset">
                      <legend>가장 아쉬웠던 건 무엇인가요? <em>필수</em></legend>
                      <div className="policy-feedback-reasons">
                        {REASONS.map((item) => (
                          <button
                            type="button"
                            key={item.value}
                            className={reason === item.value ? 'is-selected' : ''}
                            onClick={() => { setReason(item.value); setStatus(''); }}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}

                  {answer ? (
                    <label className="policy-feedback-message is-optional">
                      <span>한마디 더 남기기 <em>선택</em></span>
                      <textarea
                        value={message}
                        maxLength={3000}
                        onChange={(event) => setMessage(event.target.value)}
                        placeholder={answer === 'GOT_IT'
                          ? '예: 어떤 정보가 특히 유용했는지 알려주세요.'
                          : '예: 최근 이슈가 오래돼서 판단을 믿기 어려웠습니다.'}
                      />
                      <em>{message.length}/3000</em>
                    </label>
                  ) : null}

                  {status ? (
                    <p className="policy-feedback-status" role="status">{status}</p>
                  ) : null}
                </div>

                <footer className="policy-feedback-dialog__footer">
                  <span>
                    {source === 'PANEL' ? '패널' : '메인'} · {authenticated ? '로그인' : '비로그인'} · {trigger === 'MANUAL' ? '직접 작성' : '종료 시점'}
                  </span>
                  <div className="policy-feedback-footer-actions">
                    {trigger !== 'MANUAL' ? (
                      <button type="button" className="policy-feedback-skip" onClick={finishClose} disabled={submitting}>
                        그냥 닫기
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="policy-feedback-submit"
                      disabled={submitting || !answer || (answer !== 'GOT_IT' && !reason)}
                      onClick={() => void submit()}
                    >
                      {submitting ? '전송 중…' : '보내기'}
                    </button>
                  </div>
                </footer>
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
